import { useTranslation } from "react-i18next";
import { GeolocationService } from "@/services/geolocationService";
import {
  ContractorPanelVisitService,
  CompleteVisitData,
} from "../services/contractorPanelVisitService";
import { useToast } from "@/components/ui/use-toast";
import { useOfflineAwareMutation } from "@/lib/hooks/useOfflineAwareMutation";
import { useCachedMutation } from "@/hooks/useCachedMutation";
import { Module } from "@/constants";
import { ReadContractorVisit } from "@/types/contractorVisit";
import { getGeolocationErrorMessage } from "../utils/getGeolocationErrorMessage";
import { getSuccessMessageWithGeolocation } from "../utils/getSuccessMessageWithGeolocation";
import { useDeviceInfo } from "@/lib/hooks/useDeviceType";
import { ApiError } from "@/services/api";
import { getApiErrorDescription } from "@/lib/utils/getApiErrorDescription";
import { isGeolocationError } from "@/lib/utils/isGeolocationError";
import { DEVICE_TYPE_DESKTOP } from "@/schemas/contractorVisit";
import { DEVICE_TYPE_MOBILE } from "@/schemas/contractorVisit";

interface CompleteVisitParams {
  startDatetime?: string;
  endDatetime?: string;
}

export const useCompleteVisit = (visitId: string) => {
  const { t } = useTranslation();
  const { device } = useDeviceInfo();
  const { toast } = useToast();
  const handleOfflineAwareMutationSuccess =
    useOfflineAwareMutation<ReadContractorVisit>(Module.ContractorPanelVisits);

  return useCachedMutation({
    mutationFn: async ({ startDatetime, endDatetime }: CompleteVisitParams) => {
      const { latitude, longitude, accuracyLevel } =
        await GeolocationService.getCurrentPositionWithTimeoutFallback();

      const completeVisitData: CompleteVisitData = {
        confirmation_latitude: latitude ?? 0,
        confirmation_longitude: longitude ?? 0,
        device_type:
          device.type === DEVICE_TYPE_DESKTOP
            ? DEVICE_TYPE_DESKTOP
            : DEVICE_TYPE_MOBILE,
        confirmation_geolocation_accuracy: accuracyLevel,
      };

      if (startDatetime) {
        completeVisitData.start_datetime = startDatetime;
      }
      if (endDatetime) {
        completeVisitData.end_datetime = endDatetime;
      }

      const result = await ContractorPanelVisitService.completeVisit(
        visitId,
        completeVisitData,
      );

      return { response: result, accuracyLevel };
    },
    onSuccess: ({ response, accuracyLevel }) => {
      const baseMessage = t("contractorVisits.success.visitCompleted");
      const successMessage = getSuccessMessageWithGeolocation(
        baseMessage,
        accuracyLevel,
        t,
      );

      handleOfflineAwareMutationSuccess({
        type: "update",
        response,
        input: response,
        successMessage,
      });
    },
    onError: (error: ApiError) => {
      const apiErrorMessage = getApiErrorDescription(error, t);
      toast({
        title: t("contractorVisits.error.completeFailed"),
        description: isGeolocationError(error)
          ? getGeolocationErrorMessage(error, t)
          : apiErrorMessage,
        variant: "destructive",
      });
    },
  });
};
